import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PredictiveAddressInput } from "components/organisations/pav-address-input-v2";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import * as QueryKeys from "data";
import { fetchAddressById } from "data/queries-common";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

interface AddressSearchProps {
  onSelectAddress: ({
    longitude,
    latitude,
    geocoder_attributes,
  }: {
    longitude: number;
    latitude: number;
    geocoder_attributes: { [key: string]: string };
  }) => void;
  onClearAddress: () => void;
}

export const AddressSearch = (props: AddressSearchProps) => {
  const [expanded, setExpanded] = useState(false);
  const [address, setAddress] = useState("");
  const [addressId, setAddressId] = useState("");

  const { enqueueQueryFailed } = useCustomSnackbars();

  const addressByIdQuery = useQuery({
    queryKey: [QueryKeys.addressById, addressId],
    queryFn: () => fetchAddressById(addressId),
    enabled: !!addressId,
  });

  useEffect(() => {
    if (addressByIdQuery.isSuccess && addressByIdQuery.data) {
      setAddressId("");
      const coordinates = addressByIdQuery.data.address?.geometry?.coordinates;
      const properties = addressByIdQuery.data.address?.properties ?? {};

      const geocoder_attributes: { [key: string]: string } = {};
      Object.entries(properties).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          geocoder_attributes[key] = value;
        }
      });

      props.onSelectAddress({
        longitude: coordinates?.[0] ?? 0,
        latitude: coordinates?.[1] ?? 0,
        geocoder_attributes,
      });
    }
  }, [addressByIdQuery.isSuccess]);

  useEffect(() => {
    if (addressByIdQuery.isError) {
      enqueueQueryFailed(addressByIdQuery.error.toString());
    }
  }, [addressByIdQuery.isError]);

  return (
    <div
      className={
        expanded
          ? "flex h-12.5 w-100 items-center justify-around self-end rounded-md bg-popover pr-2 shadow-md"
          : "flex h-10 w-10 items-center justify-center self-end rounded-md bg-popover shadow-md"
      }
    >
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="search"
        className="m-2"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <X className="size-4" /> : <Search className="size-4" />}
      </Button>
      {expanded && (
        <PredictiveAddressInput
          id="address"
          name="address"
          autoFocus
          value={address}
          onChange={(value) => {
            setAddress(value);
            if (!value) {
              props.onClearAddress();
            }
          }}
          onAddressSelect={(selectedAddress, selectedId) => {
            setAddress(selectedAddress);
            setAddressId(selectedId);
          }}
          onClear={() => {
            setAddress("");
            props.onClearAddress();
          }}
        />
      )}
    </div>
  );
};
